/**
 * Header Component - Main navigation header with logo and wishlist
 */

import {
  Box,
  Column,
  Columns,
  ContentBlock,
  IconChevron,
  MenuItem,
  MenuItemLink,
  MenuRenderer,
  Text,
} from 'braid-design-system';
import type React from 'react';
import { Link, NavLink, useLocation } from 'react-router';

import { GALLERY_ENABLED } from '../../constants/features.mjs';
import { useWishlist } from '../../context/WishlistContext';

import Logo from './logo-green.png';

import * as styles from './Header.css';

const navLinks = [
  { to: '/about-us', label: 'About us' },
  { to: '/products', label: 'Products' },
  ...(GALLERY_ENABLED ? [{ to: '/gallery', label: 'Gallery' }] : []),
  { to: '/custom-orders', label: 'Custom orders' },
];

const isActivePath = (pathname: string, to: string) =>
  pathname === to || pathname.startsWith(`${to}/`);

export const Header: React.FC = () => {
  const { items } = useWishlist();
  const { pathname } = useLocation();
  const wishlistCount = items.length;

  return (
    <Box
      component="header"
      position="sticky"
      top={0}
      zIndex="sticky"
      background="customLight"
      style={{
        backgroundColor: '#fbfcee',
        boxShadow: '0 1px 0  rgba(212, 104, 122, 0.3)',
      }}
    >
      <ContentBlock width="large">
        <Box paddingX={{ mobile: 'small', wide: 'none' }}>
          <Columns space="medium" align="left" alignY="center">
            <Column>
              <Link to="/" className={styles.logo} aria-label="Fae Forest home">
                <img src={Logo} alt="Fae Forest" className={styles.logoImage} />
              </Link>
            </Column>
            <Column width="content" hideBelow="tablet">
              <Box component="nav" className={styles.nav}>
                {navLinks.map(({ to, label }) => (
                  <NavLink key={to} to={to} className={styles.navLink}>
                    {label}
                  </NavLink>
                ))}
                <NavLink to="/wishlist" className={styles.navLink}>
                  {wishlistCount > 0 ? '♥' : '♡'} Wishlist
                  {wishlistCount > 0 && (
                    <span className={styles.wishlistBadge}>
                      {wishlistCount}
                    </span>
                  )}
                </NavLink>
              </Box>
            </Column>
            <Column width="content" hideAbove="mobile">
              <MenuRenderer
                offsetSpace="small"
                trigger={(triggerProps, { open }) => (
                  <Box userSelect="none" cursor="pointer" {...triggerProps}>
                    <Text>
                      Menu{' '}
                      <IconChevron
                        direction={open ? 'up' : 'down'}
                        alignY="lowercase"
                      />
                    </Text>
                  </Box>
                )}
              >
                {navLinks.map(({ to, label }) => (
                  <MenuItemLink key={to} href={to}>
                    <span
                      className={
                        isActivePath(pathname, to)
                          ? styles.menuLinkActive
                          : undefined
                      }
                    >
                      {label}
                    </span>
                  </MenuItemLink>
                ))}
                <MenuItemLink href="/wishlist">
                  <span
                    className={
                      isActivePath(pathname, '/wishlist')
                        ? styles.menuLinkActive
                        : undefined
                    }
                  >
                    Wishlist
                  </span>
                  {wishlistCount > 0 && (
                    <span className={styles.wishlistBadge}>
                      {wishlistCount}
                    </span>
                  )}
                </MenuItemLink>
              </MenuRenderer>
            </Column>
          </Columns>
        </Box>
      </ContentBlock>
    </Box>
  );
};
